<template>
  <div class="jim-scheduling-container whatif-page">

    <!-- Header row -->
    <div class="page-header-row">
      <div class="page-header-left"></div>
      <h1 class="page-heading">JIM CAPACITY – WHAT-IF</h1>
      <div class="page-header-right"></div>
    </div>

    <div class="main-content">

      <!-- LEFT CONTROLS -->
      <div class="left-side-section">

        <div class="control" style="width:100%;">
          <label>Location</label>
          <select v-model="location" class="select-input" @change="onInputsChanged">
            <option value="ALL">ALL</option>
            <option value="F">Fresno (F)</option>
            <option value="D">Denver (D)</option>
            <option value="K">Knoxville (K)</option>
            <option value="L">Lakeland (L)</option>
          </select>
        </div>

        <!-- Machine count override -->
        <div v-if="location !== 'ALL'" class="control" style="width:100%;">
          <label>Machines in Service (Override)</label>
          <input
            type="number"
            min="0"
            v-model.number="machinesSingle"
            @input="onInputsChanged"
          />
          <div class="small-muted" style="margin-top:6px;">
            Leave blank to use baseline.
          </div>
        </div>

        <div v-else class="control" style="width:100%;">
          <label>Machines in Service (Override – ALL)</label>
          <div class="machines-grid">
            <div class="machines-cell">
              <span class="machines-label">F</span>
              <input type="number" min="0" v-model.number="machinesByLoc.F" @input="onInputsChanged" />
            </div>
            <div class="machines-cell">
              <span class="machines-label">D</span>
              <input type="number" min="0" v-model.number="machinesByLoc.D" @input="onInputsChanged" />
            </div>
            <div class="machines-cell">
              <span class="machines-label">K</span>
              <input type="number" min="0" v-model.number="machinesByLoc.K" @input="onInputsChanged" />
            </div>
            <div class="machines-cell">
              <span class="machines-label">L</span>
              <input type="number" min="0" v-model.number="machinesByLoc.L" @input="onInputsChanged" />
            </div>
          </div>
          <div class="small-muted" style="margin-top:6px;">
            Leave any box blank to use baseline for that plant.
          </div>
        </div>

        <!-- Steps: checkbox + drag reorder -->
        <div class="panel steps-panel" style="width:100%;">
          <div class="steps-title">Scenario Steps (Drag to Reorder)</div>

          <draggable
            v-model="steps"
            item-key="id"
            :animation="150"
            ghost-class="wo-ghost"
            chosen-class="wo-chosen"
            drag-class="wo-drag"
            @end="onStepsReordered"
          >
            <template #item="{ element }">
              <div class="step-card">
                <span class="step-handle" title="Drag">⠿</span>

                <label class="step-toggle">
                  <input
                    type="checkbox"
                    :checked="element.enabled"
                    @change="toggleStep(element.id, $event.target.checked)"
                  />
                  <span class="step-label">{{ element.label }}</span>
                </label>

                <span v-if="element.id === 'OT' || element.id === 'GY'" class="step-chip">
                  mutually exclusive
                </span>
              </div>
            </template>
          </draggable>

          <div class="small-muted" style="margin-top:8px;">
            Capacity is recalculated incrementally in the order shown.
          </div>
        </div>

        <button class="plan-it-btn" :disabled="loading" @click="runWhatIf">
          {{ loading ? "Loading..." : "Run What-If" }}
        </button>

        <button class="plan-it-btn" :disabled="!loaded" @click="exportCSV">
          Export CSV
        </button>

        <button class="plan-it-btn" :disabled="!loaded" @click="openPrint">
          Print PDF
        </button>


        <div v-if="error" class="panel error-box" style="margin-top:10px;">
          {{ error }}
        </div>

        <div v-if="warnings.length" class="panel warn-box" style="margin-top:10px;">
          <div class="warn-title">Notes</div>
          <div class="warn-body">
            <div v-for="(w, i) in warnings" :key="i">• {{ w }}</div>
          </div>
        </div>

      </div>

      <!-- RIGHT SIDE -->
      <div class="right-side-section">

        <div v-if="loaded" class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">Location</div>
            <div class="summary-value">{{ location }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Steps</div>
            <div class="summary-value">{{ stepsEffectiveLabel }}</div>
          </div>

          <div class="summary-item" v-if="summaryTotal">
            <div class="summary-label">Annual Potential</div>
            <div class="summary-value">{{ formatMoney0(summaryTotal.potential_annual) }}</div>
          </div>

          <div class="summary-item" v-if="summaryTotal">
            <div class="summary-label">Increase</div>
            <div class="summary-value">
              {{ formatPct(summaryTotal.increase_vs_base_pct) }}
            </div>
          </div>
        </div>

        <!-- For single loc: one table -->
        <div v-if="loaded && location !== 'ALL'" class="sheet-scroll-container">
          <div class="panel report-panel">
            <div class="table-scroll">
              <table class="whatif-table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th style="text-align:right;">Machines</th>
                    <th style="text-align:right;">Annual</th>
                    <th style="text-align:right;">Monthly</th>
                    <th style="text-align:right;">Daily</th>
                    <th style="text-align:right;">Hourly</th>
                    <th style="text-align:right;">Inc vs Prev</th>
                    <th style="text-align:right;">Inc vs Base</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, idx) in rowsSingle" :key="idx">
                    <td>{{ stageLabel(r.stage) }}</td>
                    <td style="text-align:right;">{{ r.machine_count }}</td>
                    <td style="text-align:right;">{{ formatMoney0(r.potential_annual) }}</td>
                    <td style="text-align:right;">{{ formatMoney0(r.monthly) }}</td>
                    <td style="text-align:right;">{{ formatMoney0(r.daily) }}</td>
                    <td style="text-align:right;">{{ formatMoney0(r.hourly) }}</td>
                    <td style="text-align:right;">{{ formatPct(r.increase_vs_prev_pct) }}</td>
                    <td style="text-align:right;">{{ formatPct(r.increase_vs_base_pct) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="small-muted" style="margin-top:10px;">
              Monthly = Annual / 12 • Daily = Monthly / days/month • Hourly = Daily / hours/day
            </div>
          </div>
        </div>

        <!-- For ALL: stacked tables per location -->
        <div v-else-if="loaded && location === 'ALL'" class="sheet-scroll-container">
          <div v-for="block in rowsAllBlocks" :key="block.loc" class="panel report-panel" style="margin-bottom:12px;">
            <div class="block-title">
              {{ locName(block.loc) }} ({{ block.loc }})
            </div>

            <div class="table-scroll">
              <table class="whatif-table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th style="text-align:right;">Machines</th>
                    <th style="text-align:right;">Annual</th>
                    <th style="text-align:right;">Monthly</th>
                    <th style="text-align:right;">Daily</th>
                    <th style="text-align:right;">Hourly</th>
                    <th style="text-align:right;">Inc vs Prev</th>
                    <th style="text-align:right;">Inc vs Base</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, idx) in block.rows" :key="idx">
                    <td>{{ stageLabel(r.stage) }}</td>
                    <td style="text-align:right;">{{ r.machine_count }}</td>
                    <td style="text-align:right;">{{ formatMoney0(r.potential_annual) }}</td>
                    <td style="text-align:right;">{{ formatMoney0(r.monthly) }}</td>
                    <td style="text-align:right;">{{ formatMoney0(r.daily) }}</td>
                    <td style="text-align:right;">{{ formatMoney0(r.hourly) }}</td>
                    <td style="text-align:right;">{{ formatPct(r.increase_vs_prev_pct) }}</td>
                    <td style="text-align:right;">{{ formatPct(r.increase_vs_base_pct) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-else class="panel hint-panel">
          Select a location + scenario steps, then click <b>Run What-If</b>.
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import draggable from "vuedraggable";

export default {
  name: "JimCapacityWhatIf",
  components: { draggable },

  data() {
    return {
      location: "F",

      machinesSingle: null, // single loc override
      machinesByLoc: { F: null, D: null, K: null, L: null }, // ALL overrides

      // Draggable steps (order matters)
      steps: [
        { id: "SATURDAY", label: "Add Saturday", enabled: false },
        { id: "OT", label: "Add OT", enabled: false },
        { id: "GY", label: "Add Graveyard", enabled: false },
      ],

      loading: false,
      loaded: false,
      error: null,
      warnings: [],

      result: null, // backend payload
    };
  },

  computed: {
    stepsEffective() {
      return this.steps.filter(s => s.enabled).map(s => s.id);
    },

    stepsEffectiveLabel() {
      if (!this.stepsEffective.length) return "Base only";
      const labelMap = { SATURDAY: "Saturday", OT: "OT", GY: "Graveyard" };
      return this.stepsEffective.map(x => labelMap[x] || x).join(" → ");
    },

    rowsSingle() {
      if (!this.result?.per_location?.length) return [];
      return this.result.per_location[0] || [];
    },

    rowsAllBlocks() {
      if (!this.result?.per_location?.length) return [];
      // per_location is array of arrays; each inner array contains loc rows
      // We can infer loc from first row in each block.
      return this.result.per_location
        .map(arr => ({
          loc: arr?.[0]?.loc || "?",
          rows: arr || []
        }))
        .sort((a, b) => String(a.loc).localeCompare(String(b.loc)));
    },

    // last stage row (single loc) for summary
    summaryTotal() {
      const rows = this.location === "ALL"
        ? null
        : this.rowsSingle;

      if (!rows || !rows.length) return null;
      return rows[rows.length - 1];
    }
  },

  methods: {
    // Enforce mutual exclusivity in UI (OT <-> GY)
    toggleStep(stepId, checked) {
      this.warnings = [];

      const idx = this.steps.findIndex(s => s.id === stepId);
      if (idx < 0) return;

      // set the toggle
      this.steps[idx].enabled = !!checked;

      // mutual exclusion rule
      if (checked && stepId === "OT") {
        const gy = this.steps.find(s => s.id === "GY");
        if (gy?.enabled) {
          gy.enabled = false;
          this.warnings.push("Graveyard was turned off because OT is active (mutually exclusive).");
        }
      }

      if (checked && stepId === "GY") {
        const ot = this.steps.find(s => s.id === "OT");
        if (ot?.enabled) {
          ot.enabled = false;
          this.warnings.push("OT was turned off because Graveyard is active (mutually exclusive).");
        }
      }

      this.onInputsChanged();
    },

    onStepsReordered() {
      // When order changes, we just rerun
      this.onInputsChanged();
    },

    onInputsChanged() {
      // Optional: auto-run; keeping KISS but responsive
      // Comment out if you want only manual Run button.
      this.runWhatIf();
    },

    buildParams() {
      const params = { location: this.location };

      // steps in order
      if (this.stepsEffective.length) {
        params.steps = this.stepsEffective.join(",");
      }

      // machine overrides
      if (this.location !== "ALL") {
        if (this.machinesSingle !== null && this.machinesSingle !== "" && Number.isFinite(Number(this.machinesSingle))) {
          params.machines = Number(this.machinesSingle);
        }
      } else {
        for (const loc of ["F", "D", "K", "L"]) {
          const v = this.machinesByLoc[loc];
          if (v !== null && v !== "" && Number.isFinite(Number(v))) {
            params[`machines${loc}`] = Number(v);
          }
        }
      }

      return params;
    },

    openPrint() {
    const params = new URLSearchParams();

    params.set("location", this.location);

    // steps only if enabled
    if (this.stepsEffective.length) {
        params.set("steps", this.stepsEffective.join(","));
    }

    // machines override
    if (this.location !== "ALL") {
        const v = this.machinesSingle;
        if (v !== null && v !== "" && v !== undefined && Number.isFinite(Number(v))) {
        params.set("machines", String(Number(v)));
        }
    } else {
        for (const loc of ["F","D","K","L"]) {
        const v = this.machinesByLoc[loc];
        if (v !== null && v !== "" && v !== undefined && Number.isFinite(Number(v))) {
            params.set(`machines${loc}`, String(Number(v)));
        }
        }
    }

    const url = `/jim-capacity-whatifs/print?${params.toString()}`;
    console.log("🖨️ [WHATIF] opening print URL:", url);
    window.open(url, "_blank");
    },


    

    async runWhatIf() {
      if (this.loading) return;

      this.loading = true;
      this.error = null;
      this.loaded = false;

      try {
        const params = this.buildParams();
        const { data } = await axios.get("/getJimCapacityWhatIf", { params });

        this.result = data;
        this.loaded = true;

        // If backend dropped steps due to rule, surface it
        const requested = (data.steps_requested || []).join(",");
        const effective = (data.steps_effective || []).join(",");
        if (requested && effective && requested !== effective) {
          this.warnings.push(`Pablo adjusted steps from ${requested} → ${effective} due to constraints.`);
        }
      } catch (err) {
        console.error("runWhatIf error:", err);
        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load JIM capacity what-if.";
      } finally {
        this.loading = false;
      }
    },

    stageLabel(stage) {
      if (stage === "BASE") return "Base";
      if (stage === "SATURDAY") return "Add Saturday";
      if (stage === "OT") return "Add OT";
      if (stage === "GY") return "Add Graveyard";
      return stage || "";
    },

    locName(loc) {
      if (loc === "F") return "Fresno";
      if (loc === "D") return "Denver";
      if (loc === "K") return "Knoxville";
      if (loc === "L") return "Lakeland";
      return loc;
    },

    formatMoney0(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    },

    formatPct(v) {
      if (v == null) return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return (n * 100).toFixed(1) + "%";
    },

    exportCSV() {
      if (!this.loaded || !this.result) return;

      const blocks = this.location === "ALL" ? this.rowsAllBlocks : [{ loc: this.location, rows: this.rowsSingle }];

      const rows = [];
      rows.push([
        "Location", "Stage", "Machines", "Annual", "Monthly", "Daily", "Hourly", "Inc_vs_Prev_%", "Inc_vs_Base_%"
      ]);

      for (const b of blocks) {
        for (const r of (b.rows || [])) {
          rows.push([
            b.loc,
            this.stageLabel(r.stage),
            r.machine_count,
            r.potential_annual,
            r.monthly,
            r.daily,
            r.hourly,
            r.increase_vs_prev_pct == null ? "" : (Number(r.increase_vs_prev_pct) * 100).toFixed(3),
            r.increase_vs_base_pct == null ? "" : (Number(r.increase_vs_base_pct) * 100).toFixed(3),
          ]);
        }
      }

      const csv = rows
        .map(r => r.map(val => `"${String(val ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `jim_capacity_whatif_${this.location}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  },

  mounted() {
    // initial load
    this.runWhatIf();
  }
};
</script>

<style scoped>
/* PAGE BASE */
.whatif-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

/* Base container */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* Header */
.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* Main layout */
.main-content {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* Left side */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 120px;
}

/* Right side */
.right-side-section {
  width: 100%;
  min-width: 0;
}

/* Panel base */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* Controls */
.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input,
.select-input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

/* Machines override mini grid */
.machines-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.machines-cell {
  display: flex;
  gap: 6px;
  align-items: center;
}
.machines-cell input {
  flex: 1 1 auto;
}
.machines-label {
  width: 18px;
  font-weight: 700;
  color: #cbbfbf;
  opacity: 0.9;
}

/* Buttons (match your P&L style) */
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 180px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}
.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

/* Summary bar (same vibe as P&L) */
.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.1;
}
.summary-value {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
  word-break: break-word;
}

/* Scroll container */
.sheet-scroll-container {
  max-height: 80vh;
  overflow-y: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
}

.sheet-scroll-container::-webkit-scrollbar { width: 10px; }
.sheet-scroll-container::-webkit-scrollbar-thumb { background: #444; border-radius: 8px; }
.sheet-scroll-container::-webkit-scrollbar-track { background: #111; }

/* Table panel */
.report-panel {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
}

.hint-panel {
  margin-top: 20px;
  background-color: #0b0101;
}

/* vuedraggable classes (from your MachinesView) */
.wo-ghost { opacity: 0.35; }
.wo-drag { cursor: grabbing; }

/* Steps UI */
.steps-panel { background-color: #0b0101; }
.steps-title {
  font-weight: 700;
  margin-bottom: 8px;
}
.step-card {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #444;
  background: #111;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 8px;
}
.step-handle {
  cursor: grab;
  user-select: none;
  opacity: 0.85;
  font-size: 16px;
}
.step-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex: 1 1 auto;
}
.step-label {
  font-weight: 700;
}
.step-chip {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #665;
  background: #120f08;
  color: #fef3c7;
}

/* Table */
.table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

.whatif-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
}

.whatif-table th,
.whatif-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
  white-space: nowrap;
}

.whatif-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 3;
}

.whatif-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

/* Error/warn */
.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}
.warn-box {
  border-color: #888844;
  background: #1f1a0b;
  color: #fef3c7;
  width: 100%;
}
.warn-title { font-weight: bold; margin-bottom: 6px; }

.block-title {
  font-weight: 800;
  font-size: 16px;
  color: #fff;
  margin-bottom: 8px;
}

.small-muted {
  font-size: 12px;
  opacity: 0.85;
  color: #fff;
}
</style>
